import { RHFAutocomplete, RHFTextField } from 'src/components/hook-form';
import {
  Stack,
  Typography,
  TextField,
  IconButton,
  InputAdornment,
  Button,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useContext, useEffect, useMemo, useState } from 'react';
import FormProvider from 'src/components/hook-form/form-provider';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, useWatch } from 'react-hook-form';
import { AuthContext } from 'src/auth/context/jwt';
import { getOrganizationId } from 'src/services/user.service';
import { debounce, set } from 'lodash';
import {
  createNewRole,
  editJobDetails,
  getJobDescription,
  getJobSuggestions,
  getSkillSuggestions,
} from 'src/services/role.service';
import Iconify from 'src/components/iconify';
import { useSnackbar } from 'notistack';
import JobDescription from './sections/job-description';
import { useJobCreationContext } from '../../hooks/use-job-creation-context';

export default function JobDetailsTab() {
  // context
  const context = useContext(AuthContext);
  const orgId = getOrganizationId(context.user);

  // states
  const [autoCompleteOptions, setAutoCompleteOptions] = useState([]);
  const [skillsMap, setSkillsMap] = useState([]);
  const [skillInputValue, setSkillInputValue] = useState('');
  const [autoCompleteSkillsOptions, setAutoCompleteSkillsOptions] = useState(
    [],
  );
  const [isGeneratingWithAi, setIsGeneratingWithAi] = useState(false);
  const [isGenerateWithAiButtonClicked, setIsGenerateWithAiButtonClicked] =
    useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // hooks
  const { enqueueSnackbar } = useSnackbar();
  const {
    canEdit,
    jobId: savedJobId,
    jobTitle,
    jobExperience,
    jobSkills,
    jobDescription,
    jobDetails,
    setCurrentState,
    JOB_CREATION_STATES,
    setJobId,
    setJobTitle,
    setJobExperience,
    setJobSkills,
    setJobDescription,
    setJobDetails,
    setRevalidateFunction,
    setCodingAssessments,
    setNeedCodingAssessment,
    setErrors: setValidationErrors,
  } = useJobCreationContext();

  // variables
  const defaultValues = useMemo(
    () => ({
      title: jobTitle || '',
      experience: jobExperience || '',
      skills: jobSkills || [],
      description: jobDescription || '',
    }),
    [jobDescription, jobExperience, jobSkills, jobTitle],
  );

  const schema = Yup.object().shape({
    title: Yup.string().required('Title is required'),
    experience: Yup.string().required('Experience is required'),
    skills: Yup.array().min(1, 'At least one skill is required'),
    description: Yup.string().required('Job Description is required'),
  });

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues,
    shouldFocusError: true,
    mode: 'onBlur',
  });

  const {
    reset,
    handleSubmit,
    setValue,
    getValues,
    trigger,
    control,
    formState: { errors },
  } = methods;

  const watchTitle = useWatch({
    control,
    name: 'title',
  });
  const watchExperience = useWatch({
    control,
    name: 'experience',
  });
  const watchDescription = useWatch({
    control,
    name: 'description',
  });
  const watchSkills = useWatch({
    control,
    name: 'skills',
  });

  const debouncedJobSearch = debounce(async title_prefix => {
    const payload = {
      title_prefix,
    };
    const res = await getJobSuggestions(payload, orgId);

    if (res.success) {
      const dummyAutoCompleteOptions = res.body.job_list.map((job, index) => {
        const obj = {
          id: index,
          label: job.title,
        };
        return obj;
      });
      const dumSkillsMap = res.body.job_list.map(job => job.skills);

      setSkillsMap(dumSkillsMap || []);
      setAutoCompleteOptions(dummyAutoCompleteOptions || []);
    } else {
      setAutoCompleteOptions([]);
    }
  }, 400);

  const debouncedSkillSearch = debounce(async title_prefix => {
    const payload = {
      skill_prefix: title_prefix,
    };
    const res = await getSkillSuggestions(payload, orgId);
    if (res.success) {
      setAutoCompleteSkillsOptions(res.body.skills || []);
    } else {
      setAutoCompleteSkillsOptions([]);
    }
  }, 400);

  // functions
  const handleOptionSelect = async (e, newValue) => {
    setAutoCompleteOptions([]);
    const _skillsMap = skillsMap.length
      ? skillsMap
      : [
          ['Sales', 'Networking', 'Communication'],
          ['SEO', 'Analytics', 'Keyword Research'],
          ['Communication', 'Problem-solving', 'Multitasking'],
        ];
    methods.setValue('title', newValue?.label || '');
    methods.setValue('skills', _skillsMap[newValue?.id] || []);
  };

  const handleGenerateWithAi = async () => {
    try {
      setIsGeneratingWithAi(true);

      const descPayload = {
        title: getValues().title,
      };
      const res = await getJobDescription(descPayload, orgId);

      if (res.success) {
        methods.setValue(
          'description',
          `${res.body.job_description}`.replaceAll('\n', '<br>'),
        );
        methods.setValue('skills', res.body.skills);
      } else throw new Error(res.message);
    } catch (err) {
      console.log(err);
      enqueueSnackbar({
        message: `Something went wrong`,
        variant: 'error',
      });
    } finally {
      setIsGeneratingWithAi(false);
      setIsGenerateWithAiButtonClicked(true);
    }
  };

  const handleJobChange = e => {
    setValue('title', e.target.value);
    if (e.target.value.length > 0) debouncedJobSearch(e.target.value);
  };

  const handleIncrement = () => {
    const currentExperience = parseInt(getValues('experience'), 10) || 0;
    setValue('experience', `${currentExperience + 1}`);
  };

  const handleDecrement = () => {
    const currentExperience = parseInt(getValues('experience'), 10) || 0;
    if (currentExperience > 0) {
      setValue('experience', `${currentExperience - 1}`);
    }
  };

  const handleSkillChange = e => {
    debouncedSkillSearch(e.target.value);
  };

  const createNewJob = handleSubmit(async data => {
    try {
      setIsSubmitting(true);
      if (savedJobId) {
        const payload = {};
        payload.job_description = data.description;

        if (canEdit) {
          payload.name = data.title;
          payload.min_experience = data.experience;
          payload.skills = data.skills;
        }

        const editJobDetailsResponse = await editJobDetails(
          payload,
          orgId,
          savedJobId,
        );

        if (editJobDetailsResponse.success) {
          enqueueSnackbar('Job details saved successfully');
          setCurrentState(JOB_CREATION_STATES.AUTOPILOT_WORKFLOW.value);
        } else throw new Error('Error while saving job details');
      } else if (!savedJobId) {
        let formattedDescription = '';

        if (data.description)
          formattedDescription = `${data.description}`.replaceAll('<br>', '\n');

        const roleData = {
          name: data.title,
          skills: data?.skills,
          min_experience: data?.experience,
          job_description: formattedDescription,
        };

        const createNewRoleResponse = await createNewRole(roleData, orgId);

        if (createNewRoleResponse.success) {
          setJobId(createNewRoleResponse.body.job_opening_id);
          setJobDetails(createNewRoleResponse.body);

          if (createNewRoleResponse.body.need_coding_assessment === true) {
            setNeedCodingAssessment(true);
            const languages = createNewRoleResponse.body.job_coding_languages;

            setCodingAssessments([
              {
                questions: [
                  {
                    text: '',
                    testCases: [],
                    timeLimit: 30,
                  },
                ],
                languages,
              },
            ]);
          }

          enqueueSnackbar('Job Saved Successfully');
          setCurrentState(JOB_CREATION_STATES.AUTOPILOT_WORKFLOW.value);
        }
        reset();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  });

  useEffect(() => {
    setJobTitle(watchTitle);
    setJobExperience(watchExperience);
    setJobSkills(watchSkills);
    setJobDescription(watchDescription);
  }, [
    jobDescription,
    jobExperience,
    setJobDescription,
    setJobExperience,
    setJobSkills,
    setJobTitle,
    watchDescription,
    watchExperience,
    watchSkills,
    watchTitle,
  ]);

  useEffect(() => {
    if (watchDescription === '<p><br></p>') {
      setValue('description', '');
    }
  }, [methods, setValue, watchDescription]);

  useEffect(() => {
    setRevalidateFunction(async () => trigger());

    return () => {
      setRevalidateFunction(null);
    };
  }, [setRevalidateFunction, trigger]);

  useEffect(() => {
    setValidationErrors(errors);

    return () => {
      setValidationErrors({});
    };
  }, [errors, setValidationErrors]);

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(createNewJob)}>
      <Stack spacing={4} width="78%">
        <Stack spacing={4}>
          <Stack spacing={2}>
            <Typography variant="subheading1">Job Role</Typography>
            <RHFAutocomplete
              freeSolo
              name="title"
              disabled={!canEdit}
              onChange={handleOptionSelect}
              options={
                autoCompleteOptions.length > 0
                  ? autoCompleteOptions
                  : [
                      {
                        id: 0,
                        label: 'Business Development Executive',
                      },
                      {
                        id: 1,
                        label: 'SEO Analyst',
                      },
                      {
                        id: 2,
                        label: 'Customer Support Executive',
                      },
                    ]
              }
              autoHighlight
              getOptionLabel={option => option?.label || getValues().title}
              renderInput={params => (
                <TextField
                  sx={{
                    fontSize: '0.875rem',
                  }}
                  placeholder="Eg. Software Engineer"
                  onChange={handleJobChange}
                  {...params}
                />
              )}
            />
          </Stack>
          <Stack display={!watchTitle && 'none'} spacing={2}>
            <Typography variant="subheading1">Minimum Experience</Typography>
            <RHFTextField
              name="experience"
              placeholder="Eg. 1"
              disabled={!canEdit}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Stack alignItems="center" direction="row" spacing={1}>
                      <Stack>
                        <IconButton
                          onClick={handleIncrement}
                          size="small"
                          sx={{
                            padding: 0,
                          }}
                        >
                          <Iconify width={16} icon="tabler:chevron-up" />
                        </IconButton>
                        <IconButton
                          onClick={handleDecrement}
                          size="small"
                          sx={{
                            padding: 0,
                          }}
                        >
                          <Iconify width={16} icon="tabler:chevron-down" />
                        </IconButton>
                      </Stack>
                      <Typography variant="input">In Years</Typography>
                    </Stack>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
          {watchExperience ? (
            <JobDescription
              isGeneratingWithAi={isGeneratingWithAi}
              setIsGeneratingWithAi={setIsGeneratingWithAi}
              skillInputValue={skillInputValue}
              setSkillInputValue={setSkillInputValue}
              handleSkillChange={handleSkillChange}
              methods={methods}
              handleGenerateWithAi={handleGenerateWithAi}
              autoCompleteSkillsOptions={autoCompleteSkillsOptions}
              isGenerateWithAiButtonClicked={isGenerateWithAiButtonClicked}
              setIsGenerateWithAiButtonClicked={
                setIsGenerateWithAiButtonClicked
              }
            />
          ) : null}
          {watchDescription && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <LoadingButton
                loading={isSubmitting}
                loadingPosition="start"
                variant="contained"
                type="submit"
                fullWidth
              >
                {isSubmitting ? 'Saving...' : 'Continue'}
              </LoadingButton>
            </Stack>
          )}
        </Stack>
      </Stack>
    </FormProvider>
  );
}
